/**
 * Session storage for the active tab plus optional localStorage for new tabs
 * and browser restarts. Enabled by default; turn off for QA-only session runs.
 */

const DURABLE_PREF_KEY = "exxat-ds:question-history-durable:v1"

export function isDurableQuestionHistoryEnabled(): boolean {
  if (typeof window === "undefined") return false
  try {
    const pref = window.localStorage.getItem(DURABLE_PREF_KEY)
    if (pref === "0") return false
    return true
  } catch {
    return false
  }
}

export function setDurableQuestionHistoryEnabled(enabled: boolean): void {
  if (typeof window === "undefined") return
  try {
    if (enabled) window.localStorage.removeItem(DURABLE_PREF_KEY)
    else window.localStorage.setItem(DURABLE_PREF_KEY, "0")
  } catch {
    /* private mode */
  }
}

function readRaw(store: Storage, key: string): string | null {
  try {
    return store.getItem(key)
  } catch {
    return null
  }
}

function writeRaw(store: Storage, key: string, value: string): void {
  try {
    store.setItem(key, value)
  } catch {
    /* quota / private mode */
  }
}

function removeRaw(store: Storage, key: string): void {
  try {
    store.removeItem(key)
  } catch {
    /* ignore */
  }
}

/** Read JSON from session, then localStorage when session is empty. */
export function readTieredJson<T>(
  key: string,
  isValid: (value: unknown) => value is T,
): T | null {
  if (typeof window === "undefined") return null
  const parse = (raw: string | null): T | null => {
    if (!raw) return null
    try {
      const parsed = JSON.parse(raw) as unknown
      return isValid(parsed) ? parsed : null
    } catch {
      return null
    }
  }
  const fromSession = parse(readRaw(window.sessionStorage, key))
  if (fromSession) return fromSession
  if (!isDurableQuestionHistoryEnabled()) return null
  return parse(readRaw(window.localStorage, key))
}

/**
 * Merge session and local records when both exist (e.g. two tabs). Keeps the
 * richer revision history per key.
 */
export function readTieredJsonMerged<T extends Record<string, unknown>>(
  key: string,
  isValid: (value: unknown) => value is T,
  mergeEntries: (left: T[string], right: T[string]) => T[string],
): T | null {
  if (typeof window === "undefined") return null
  const parse = (raw: string | null): T | null => {
    if (!raw) return null
    try {
      const parsed = JSON.parse(raw) as unknown
      return isValid(parsed) ? parsed : null
    } catch {
      return null
    }
  }
  const session = parse(readRaw(window.sessionStorage, key))
  const local =
    isDurableQuestionHistoryEnabled()
      ? parse(readRaw(window.localStorage, key))
      : null
  if (!session && !local) return null
  if (!session) return local
  if (!local) return session
  const keys = new Set([...Object.keys(session), ...Object.keys(local)])
  const merged = {} as T
  for (const entryKey of keys) {
    const left = session[entryKey]
    const right = local[entryKey]
    if (left && right) {
      merged[entryKey as keyof T] = mergeEntries(left, right) as T[keyof T]
    } else {
      merged[entryKey as keyof T] = (left ?? right) as T[keyof T]
    }
  }
  return merged
}

/** Write to session always; mirror to localStorage when durable mode is on. */
export function writeTieredJson(key: string, value: unknown): void {
  if (typeof window === "undefined") return
  const payload = JSON.stringify(value)
  writeRaw(window.sessionStorage, key, payload)
  if (isDurableQuestionHistoryEnabled()) {
    writeRaw(window.localStorage, key, payload)
  }
}

export function removeTieredJson(key: string): void {
  if (typeof window === "undefined") return
  removeRaw(window.sessionStorage, key)
  removeRaw(window.localStorage, key)
}

export function removeTieredJsonByMark(mark: string): void {
  if (typeof window === "undefined") return
  for (const store of [window.sessionStorage, window.localStorage]) {
    const keys: string[] = []
    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index)
      if (key?.includes(mark)) keys.push(key)
    }
    for (const key of keys) removeRaw(store, key)
  }
}

function revisionCount(row: { revisions?: unknown[] }): number {
  return row.revisions?.length ?? 0
}

/** Prefer the overlay or bank row with more authored revisions. */
export function mergeHistoryRicher<T extends { revisions?: unknown[] }>(
  left: T,
  right: T,
): T {
  if (revisionCount(left) > revisionCount(right)) return { ...right, ...left }
  if (revisionCount(right) > revisionCount(left)) return { ...left, ...right }
  return { ...left, ...right }
}
